import type { RumEvent, RumInitConfiguration } from "@datadog/browser-rum";

const CHUNK_NAME = /\/customer-app-assets\/(.+)-[\w-]{8}\.(?:js|css)$/;

/** Bundle chunk name with the content hash stripped, e.g. `useDetectDeviceSize-Drr30Rev.js` -> `useDetectDeviceSize`. */
const chunkNameFromUrl = (url: string): string | undefined => CHUNK_NAME.exec(url)?.[1];

/**
 * Error messages that never come from our own code: Chrome raises this one when a browser extension's content
 * script calls `runtime.sendMessage` and its own background script doesn't answer. The extension injects into every
 * page, so Datadog reports the uncaught error against ours.
 */
const EXTENSION_NOISE_ERRORS = [/^Could not establish connection\. Receiving end does not exist\.$/];

const isExtensionNoise = (event: RumEvent): boolean =>
  event.type === "error" && EXTENSION_NOISE_ERRORS.some((pattern) => pattern.test(event.error.message));

/**
 * RUM `beforeSend` that tags view and resource events with `context.initial_load` (does the event belong to the
 * page's initial-load view) and resource events with `context.chunk` (stable chunk name). Resource events don't
 * carry the view's `loading_type`, and chunk URLs change every deploy, so neither can be queried in Datadog
 * otherwise. Views are tagged too so per-load ratios only count views this hook has seen. Errors raised by browser
 * extensions are dropped before any of that, so they can't seed `initialViewId`.
 */
export const createRumBeforeSend = (): NonNullable<RumInitConfiguration["beforeSend"]> => {
  let initialViewId: string | undefined;

  return (event: RumEvent): boolean => {
    if (isExtensionNoise(event)) {
      return false;
    }

    if (event.type === "view" && event.view.loading_type === "initial_load") {
      initialViewId = event.view.id;
    }
    initialViewId ??= event.view.id;
    const initialLoad = event.view.id === initialViewId;

    if (event.type === "view") {
      event.context = { ...event.context, initial_load: initialLoad };
    }
    if (event.type === "resource") {
      const chunk = chunkNameFromUrl(event.resource.url);
      event.context = { ...event.context, initial_load: initialLoad, ...(chunk ? { chunk } : {}) };
    }
    return true;
  };
};
